<script lang="ts">
	import { page } from "$app/state";
	import type { Author } from "#lib/types/shared.js";
	import type { Breadcrumb } from "#lib/types/breadcrumb.js";
	import type { DatasetSchema, FaqItem, ListItem } from "./types/metaData";
	import { SITE_ORIGIN, canonicalUrl } from "#lib/siteUrl.js";

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	type JsonLdValue = any;
	type JsonLdObject = Record<string, JsonLdValue>;

	interface Props {
		data?: {
			title?: string | null;
			description?: string | null;
			author?: Author[] | null;
			image?: string | null;
			imageAlt?: string | null;
			type?: string | null;
			datePublished?: string | null;
			dateModified?: string | null;
			mainEntity?: unknown | null;
			category?: string | null;
			price?: number | null;
			priceValidUntil?: string | null;
			campaign?: {
				name: string | null;
				price: number | null;
				validFrom?: string | null;
				validThrough?: string | null;
			} | null;
		} | null;
		faq?: FaqItem[] | null;
		reviews?:
			{ title: string; body: string; stars: number; date?: string; author?: string }[] | null;
		includeOrganization?: boolean;
		breadcrumbs?: Breadcrumb[] | null;
		itemList?: ListItem[] | null;
		/** Dataset schema for pages presenting structured data (e.g. spot prices) */
		dataset?: DatasetSchema | null;
	}

	let {
		data = null,
		faq = null,
		reviews = null,
		includeOrganization = false,
		breadcrumbs = null,
		itemList = null,
		dataset = null,
	}: Props = $props();

	// Every `@id` below is an entity identifier, so it has to be the canonical
	// spelling of the page — not the host this particular request came in on.
	const pageUrl = $derived(canonicalUrl(page.url?.pathname));
	const ORGANIZATION_ID = `${SITE_ORIGIN}/#organization`;
	const LOGO_URL =
		"https://cdn.sanity.io/images/n5xeg4df/ishavskraft-001/39a8d3727edb4bce63f9f5afc2500190303e86f5-636x126.png";

	// Same fallback shape as MetaData's `getTitle`, so `headline` and `<title>`
	// don't disagree on pages that set no title of their own.
	let title = $derived(
		data?.title || `Ishavskraft${page?.url?.pathname !== "/" ? ` – ${page?.url?.pathname}` : ""}`
	);

	// ──────────────────────────────────────────────
	// Schema builders — one function per type
	// ──────────────────────────────────────────────

	const organizationSchema = (): JsonLdObject => ({
		"@type": "Organization",
		"@id": ORGANIZATION_ID,
		name: "Ishavskraft",
		legalName: "Ishavskraft AS",
		parentOrganization: {
			"@type": "Organization",
			name: "Troms kraft AS",
			parentOrganization: {
				"@type": "Organization",
				name: "Troms Holding AS",
				parentOrganization: {
					"@type": "Organization",
					name: "Troms Fylkeskommune",
				},
			},
		},
		url: SITE_ORIGIN,
		logo: { "@type": "ImageObject", url: LOGO_URL },
		description:
			"Ishavskraft er en norsk strømleverandør som tilbyr strømavtaler til privatkunder og bedrifter. Vi leverer strøm i hele Norge.",
		foundingDate: "1997-06-26",
		taxID: "NO 979 139 268 MVA",
		knowsLanguage: "nb",
		areaServed: "NO",
		address: {
			"@type": "PostalAddress",
			streetAddress: "Markveien 6",
			addressLocality: "Alta",
			postalCode: "9510",
			addressCountry: "NO",
		},
		contactPoint: [
			{
				"@type": "ContactPoint",
				telephone: "+4777612000",
				contactType: "customer service",
				areaServed: "NO",
				availableLanguage: "Norwegian",
			},
			{
				"@type": "ContactPoint",
				email: "kunde@ishavskraft.no",
				contactType: "customer service",
				areaServed: "NO",
				availableLanguage: "Norwegian",
			},
		],
		sameAs: [
			"https://www.facebook.com/ishavskraft",
			"https://www.linkedin.com/company/ishavskraft",
		],
	});

	const websiteSchema = (): JsonLdObject => ({
		"@type": "WebSite",
		"@id": `${SITE_ORIGIN}/#website`,
		url: SITE_ORIGIN,
		name: title,
		description: data?.description || undefined,
		publisher: { "@id": ORGANIZATION_ID },
	});

	const breadcrumbSchema = (): JsonLdObject | null => {
		if (!breadcrumbs?.length || breadcrumbs.length < 2) return null;
		return {
			"@type": "BreadcrumbList",
			itemListElement: breadcrumbs.map((crumb, index) => ({
				"@type": "ListItem",
				position: index + 1,
				name: crumb.label,
				item: crumb.href.startsWith("http") ? crumb.href : `${SITE_ORIGIN}${crumb.href}`,
			})),
		};
	};

	const datasetSchema = (): JsonLdObject | null => {
		if (!dataset) return null;
		const obj: JsonLdObject = {
			"@type": "Dataset",
			"@id": `${pageUrl}#dataset`,
			name: dataset.name,
			creator: { "@id": ORGANIZATION_ID, "@type": "Organization" },
			license: dataset.license || "https://creativecommons.org/licenses/by/4.0/",
			temporalCoverage: dataset.temporalCoverage || "..",
			spatialCoverage: {
				"@type": "Place",
				name: dataset.spatialCoverage || "Norge",
			},
			distribution: {
				"@type": "DataDownload",
				encodingFormat: "text/html",
				contentUrl: pageUrl,
			},
		};
		if (dataset.description) obj.description = dataset.description;
		if (dataset.variableMeasured?.length) {
			obj.variableMeasured = dataset.variableMeasured.map((v) => ({
				"@type": "PropertyValue",
				name: v.name,
				...(v.unitText ? { unitText: v.unitText } : {}),
				...(v.value != null ? { value: v.value } : {}),
			}));
		}
		return obj;
	};

	const faqSchema = (): JsonLdObject | null => {
		if (!faq?.length) return null;

		const mainType = data?.type
			? `${data.type.charAt(0).toUpperCase()}${data.type.slice(1)}`
			: "WebPage";

		const obj: JsonLdObject = {
			"@type": "FAQPage",
			mainEntity: faq.map((item) => ({
				"@type": "Question",
				name: item.question,
				acceptedAnswer: { "@type": "Answer", text: item.answer },
			})),
		};

		obj["@id"] = `${pageUrl}#faq`;
		obj.about = { "@id": `${pageUrl}#${mainType.toLowerCase()}` };

		return obj;
	};

	const itemListSchema = (): JsonLdObject | null => {
		if (!itemList?.length) return null;
		return {
			"@type": "ItemList",
			itemListElement: itemList.map((item, i) => ({
				"@type": "ListItem",
				position: i + 1,
				item,
			})),
		};
	};

	const articleSchema = (obj: JsonLdObject): JsonLdObject => {
		obj.headline = title;
		obj.publisher = {
			"@type": "Organization",
			name: "Ishavskraft",
			logo: { "@type": "ImageObject", url: LOGO_URL },
		};
		if (data?.description) obj.description = data.description;
		if (data?.image) obj.image = data.image;
		if (data?.datePublished) obj.datePublished = new Date(data.datePublished).toISOString();
		if (data?.dateModified) obj.dateModified = new Date(data.dateModified).toISOString();
		if (data?.author) {
			obj.author = data.author.map((a) => ({
				"@type": "Person",
				name: a.name,
				jobTitle: a.title,
				url: a.profileLink || undefined,
			}));
		}
		return obj;
	};

	const productSchema = (obj: JsonLdObject): JsonLdObject => {
		obj.brand = { "@type": "Brand", name: "Ishavskraft" };
		if (data?.category) obj.category = data.category;
		if (data?.description) obj.description = data.description;
		if (data?.image) obj.image = data.image;

		if (reviews?.length) {
			const avg = reviews.reduce((sum, r) => sum + r.stars, 0) / reviews.length;
			obj.aggregateRating = {
				"@type": "AggregateRating",
				ratingValue: Math.round(avg * 10) / 10,
				bestRating: 5,
				ratingCount: reviews.length,
			};
			obj.review = reviews.map((r) => ({
				"@type": "Review",
				name: r.title,
				reviewBody: r.body,
				reviewRating: { "@type": "Rating", ratingValue: r.stars, bestRating: 5 },
				...(r.date ? { datePublished: r.date } : {}),
				...(r.author ? { author: { "@type": "Person", name: r.author } } : {}),
			}));
		}

		// No price data — valid Product schema without offers (e.g. spot price contracts)
		if (data?.price == null && !data?.campaign) return obj;

		const defaultPriceValidUntil = new Date(new Date().getFullYear(), 11, 31).toISOString();
		const seller = { "@type": "Organization", name: "Ishavskraft", url: SITE_ORIGIN };

		const ordinary: JsonLdValue = {
			"@type": "Offer",
			url: pageUrl,
			availability: "https://schema.org/InStock",
			priceCurrency: "NOK",
			price: data?.price?.toFixed(2) || "0",
			priceValidUntil: data?.priceValidUntil || defaultPriceValidUntil,
			seller,
		};
		if (data?.campaign) ordinary.name = "Ordinær pris";
		obj.offers = [ordinary];

		if (data?.campaign) {
			const campaign: JsonLdValue = {
				"@type": "Offer",
				url: pageUrl,
				name: data.campaign.name || "Kampanjepris",
				price: data.campaign.price?.toFixed(2) ?? "",
				priceCurrency: "NOK",
				availability: "https://schema.org/InStock",
				priceValidUntil: data.campaign.validThrough || defaultPriceValidUntil,
				seller,
			};
			if (data.campaign.validFrom)
				campaign.validFrom = new Date(data.campaign.validFrom).toISOString();
			if (data.campaign.validThrough)
				campaign.validThrough = new Date(data.campaign.validThrough).toISOString();
			obj.offers.push(campaign);
		}

		return obj;
	};

	const faqPageSchema = (obj: JsonLdObject): JsonLdObject => {
		if (faq?.length) {
			obj.mainEntity = faq.map((item) => ({
				"@type": "Question",
				name: item.question,
				acceptedAnswer: { "@type": "Answer", text: item.answer },
			}));
		}
		if (data?.description) obj.description = data.description;
		if (data?.image) obj.image = data.image;
		if (data?.datePublished) obj.datePublished = new Date(data.datePublished).toISOString();
		// Add one or more authors
		if (data?.author) {
			obj.author = data.author.map((a) => ({
				"@type": "Person",
				name: a.name,
				jobTitle: a.title,
				url: a.profileLink || undefined,
			}));
		}
		return obj;
	};

	const webPageSchema = (obj: JsonLdObject): JsonLdObject => {
		if (data?.description) obj.description = data.description;
		if (data?.image) obj.image = data.image;
		obj["isPartOf"] = { "@id": `${SITE_ORIGIN}/#website` };
		return obj;
	};

	// ──────────────────────────────────────────────
	// Main entity builder — delegates to type-specific functions
	// ──────────────────────────────────────────────

	const mainEntitySchema = (): JsonLdObject => {
		const type = data?.type
			? `${data.type.charAt(0).toUpperCase()}${data.type.slice(1)}`
			: "WebPage";

		const hasFaq = !!faq?.length;
		const isFaqPage = type.toLowerCase() === "faqpage";

		let obj: JsonLdObject = {
			"@type": type,
			name: title,
		};

		obj.url = pageUrl;

		// Link to separate FAQ entity if FAQ exists but page isn't a FAQPage
		if (!isFaqPage && hasFaq) {
			obj["@id"] = `${pageUrl}#${type.toLowerCase()}`;
			obj.subjectOf = { "@id": `${pageUrl}#faq` };
		}

		// Delegate to type-specific builder
		if (type.match(/product/i)) return productSchema(obj);
		if (type.match(/article/i)) return articleSchema(obj);
		if (isFaqPage) return faqPageSchema(obj);
		return webPageSchema(obj);
	};

	// ──────────────────────────────────────────────
	// Graph assembly
	// ──────────────────────────────────────────────

	const buildGraph = (): JsonLdObject[] => {
		const entities: JsonLdObject[] = [];
		const isWebsite = data?.type?.toLowerCase() === "website";
		const isFaqPage = data?.type?.toLowerCase() === "faqpage";

		if (includeOrganization) entities.push(organizationSchema());

		if (isWebsite) {
			entities.push(websiteSchema());
		} else {
			entities.push(mainEntitySchema());
		}

		// Separate FAQ entity when page isn't already a FAQPage
		if (!isFaqPage && !isWebsite) {
			const faqEntity = faqSchema();
			if (faqEntity) entities.push(faqEntity);
		}

		const breadcrumbEntity = breadcrumbSchema();
		if (breadcrumbEntity) entities.push(breadcrumbEntity);

		const itemListEntity = itemListSchema();
		if (itemListEntity) entities.push(itemListEntity);

		const datasetEntity = datasetSchema();
		if (datasetEntity) entities.push(datasetEntity);

		return entities;
	};

	let jsonLdScript = $derived(
		`<script type="application/ld+json">${JSON.stringify({ "@context": "https://schema.org", "@graph": buildGraph() })}${decodeURI(`%3C/script%3E`)}`
	);
</script>

<svelte:head>
	<!-- eslint-disable-next-line svelte/no-at-html-tags -->
	{@html jsonLdScript}
</svelte:head>
